import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type ContainerRow, type Permissions, type SlackStatus } from "@/lib/api";
import { booChatStatus, type ConnectionStatus } from "@/lib/connection-status";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ConnectSlackDialog } from "@/pages/org-connections/WorkspaceSlack";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { BooAppActions, BooAppPanel } from "./BooApp";
import {
  ConnectionHeader,
  ConnectionSection,
  DetailList,
  DetailRow,
  DisconnectButton,
  StatusLine,
} from "./ConnectionParts";
import type { ConnectionDef } from "./connections-lib";
import { secondBooGuide, type GuideStep } from "./second-boo-copy";
import { useBooApp, type BooApp } from "./use-boo-app";

// Slack has two ways in: the Boo app (appears as Boo, one Boo per Slack
// workspace) or the Boo's own Slack app (its own name and icon, made from the
// workspace token linked in the org's Connections → Slack). The own app wins
// when both are set. A workspace already answering through another Boo gets
// a step-by-step guide to the own app, with moving the Boo app tucked away.
export function SlackConnection({
  def,
  container,
  permissions,
}: {
  def: ConnectionDef;
  container: ContainerRow;
  permissions: Permissions;
}) {
  const queryClient = useQueryClient();
  const app = useBooApp("slack", container);
  const [ownAppOpen, setOwnAppOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const canConnect = permissions?.canConnectSlack !== false;
  const returnTo = `/orgs/${container.organizationId}/containers/${container.id}/connections/slack`;

  const customQuery = useQuery({
    queryKey: ["slack", container.id],
    // sync=true reads the live app name from Slack so a rename done directly in
    // Slack's UI shows up here. Runs once on mount (page load / refresh):
    // staleTime:Infinity + no focus refetch keeps it from polling the Tier-3
    // export API.
    queryFn: () => api.slack.custom.status(container.id, true),
    enabled: container.deploymentStatus === "running",
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const workspaceQuery = useQuery({
    queryKey: ["slack-workspace"],
    queryFn: () => api.slack.custom.workspace.status(),
  });
  const installOwn = useMutation({
    mutationFn: (name: string) => api.slack.custom.install(container.id, name, returnTo),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });
  const disconnectOwn = useMutation({
    mutationFn: () => api.slack.custom.disconnect(container.id),
    onSuccess: () => {
      queryClient.setQueryData(["slack", container.id], { connected: false });
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      setDisconnectOpen(false);
    },
  });

  const own = customQuery.data;
  const ownConnected = own?.connected ?? !!container.slackTeamId;
  const workspaceReady = workspaceQuery.data?.connected === true;
  const status: ConnectionStatus = ownConnected
    ? {
        loading: false,
        state: "connected",
        label: `Your app${own?.botName ? ` “${own.botName}”` : ""} in ${own?.teamName ?? "your workspace"}`,
      }
    : booChatStatus(app.data, app.query.isLoading, app.place, container.id);

  const header = (
    <ConnectionHeader
      def={def}
      status={<StatusLine status={status} className="h-5 text-sm" />}
      actions={
        ownConnected ? (
          canConnect && <DisconnectButton pending={disconnectOwn.isPending} onClick={() => setDisconnectOpen(true)} />
        ) : (
          <BooAppActions app={app} container={container} />
        )
      }
    />
  );

  if (app.query.isLoading) {
    return (
      <>
        {header}
        <LoadingIndicator message="Checking Slack…" />
      </>
    );
  }
  if (app.query.isError || !app.data) {
    return (
      <>
        {header}
        <InlineError error={app.query.error ?? "Failed to check Slack status"} />
      </>
    );
  }

  const ownAppFormFor = (autoFocus = false) => (
    <OwnAppForm
      container={container}
      workspaceReady={workspaceReady}
      workspaceLoading={workspaceQuery.isLoading}
      workspaceError={workspaceQuery.isError ? workspaceQuery.error : null}
      install={installOwn}
      autoFocus={autoFocus}
    />
  );
  const ownAppForm = ownAppFormFor();

  return (
    <>
      {header}
      {ownConnected ? (
        <OwnAppDetails container={container} status={own} />
      ) : app.state === "here" ? (
        <>
          <ConnectionSection title="Boo app" description="Boo answers in Slack as Boo, with the Boo logo.">
            <BooAppPanel app={app} usage="Message it directly, or @mention it in any public channel." pitch={null} />
          </ConnectionSection>
          {canConnect && workspaceReady && (
            <ConnectionSection
              title="Your own app"
              description="Give this Boo its own name in Slack instead of Boo. You can set its icon in Slack afterward."
            >
              {ownAppOpen ? (
                ownAppForm
              ) : (
                <Button size="sm" variant="outline" className="w-fit" onClick={() => setOwnAppOpen(true)}>
                  Switch to your own app
                </Button>
              )}
            </ConnectionSection>
          )}
        </>
      ) : app.state === "elsewhere" ? (
        <SecondBooGuide
          app={app}
          canConnect={canConnect}
          workspaceTeam={workspaceQuery.data?.teamName ?? null}
          canLink={workspaceQuery.data?._permissions?.canInstall !== false}
          ownAppForm={ownAppFormFor}
          workspaceReady={workspaceReady}
          workspaceLoading={workspaceQuery.isLoading}
          workspaceError={workspaceQuery.isError ? workspaceQuery.error : null}
        />
      ) : (
        <ConnectionSection title="How Boo joins Slack" description="Pick one. You can switch later.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Choice title="Boo app" tag={{ label: "Quickest", tone: "emerald" }}>
              <BooAppPanel
                app={app}
                usage=""
                pitch={
                  <>
                    Boo appears in Slack as <strong>Boo</strong>, with the Boo logo. Anyone in your workspace can
                    message it. One Slack workspace connects one Boo this way.
                  </>
                }
              />
            </Choice>
            <Choice title="Your own app" tag={null}>
              <p className="text-sm text-muted-foreground">
                Boo appears under a name you pick, like <strong>Acme Assistant</strong>, and you can set its icon in
                Slack afterward. Each Boo can have its own.
              </p>
              {canConnect &&
                (ownAppOpen ? (
                  ownAppForm
                ) : (
                  <Button size="sm" variant="outline" className="w-fit" onClick={() => setOwnAppOpen(true)}>
                    Set up your own app
                  </Button>
                ))}
            </Choice>
          </div>
        </ConnectionSection>
      )}
      {disconnectOwn.isError && <InlineError error={disconnectOwn.error} />}
      <ConfirmDialog
        open={disconnectOpen}
        onOpenChange={setDisconnectOpen}
        title="Disconnect your Slack app?"
        description="This deletes the Slack app from your workspace. Slack messages stop reaching this Boo."
        confirmLabel="Disconnect"
        loading={disconnectOwn.isPending}
        onConfirm={() => disconnectOwn.mutate()}
      />
    </>
  );
}

const TAG_TONE = {
  emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
} as const;

// Another Boo answers in this workspace through the Boo app, so this Boo joins
// as its own Slack app: link the workspace (once), name it, set its icon.
// Taking the Boo app from the other Boo stays available, folded away.
function SecondBooGuide({
  app,
  canConnect,
  canLink,
  workspaceTeam,
  workspaceReady,
  workspaceLoading,
  workspaceError,
  ownAppForm,
}: {
  app: BooApp;
  canConnect: boolean;
  canLink: boolean;
  workspaceTeam: string | null;
  workspaceReady: boolean;
  workspaceLoading: boolean;
  workspaceError: unknown;
  ownAppForm: (autoFocus: boolean) => ReactNode;
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  // Set once the viewer opens the token dialog, so the name field takes focus
  // when linking finishes instead of focus dropping to the page.
  const [linkedHere, setLinkedHere] = useState(false);
  const guide = secondBooGuide({
    holderName: app.data?.containerName ?? null,
    place: app.place ?? "Your Slack workspace",
    workspaceReady,
    workspaceTeam,
    canLink,
    canConnect,
  });
  const action = (step: GuideStep) =>
    step.action === "link" ? (
      <Button
        size="sm"
        className="w-fit"
        onClick={() => {
          setLinkedHere(true);
          setLinkOpen(true);
        }}
      >
        Link your workspace
      </Button>
    ) : step.action === "form" ? (
      ownAppForm(linkedHere)
    ) : null;
  const detail = (step: GuideStep) => {
    if (!step.link) return step.detail;
    const [before, after] = step.detail.split(step.link.text);
    return (
      <>
        {before}
        <a
          href={step.link.href}
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          {step.link.text}
        </a>
        {after}
      </>
    );
  };

  return (
    <>
      <ConnectionSection title={guide.title} description={guide.why}>
        {workspaceLoading ? (
          <LoadingIndicator message="Checking your workspace…" />
        ) : workspaceError ? (
          <InlineError error={workspaceError} />
        ) : (
          // role="list" keeps list semantics in Safari once Tailwind removes the markers.
          <ol role="list" className="flex flex-col divide-y rounded-lg border bg-background">
            {guide.steps.map((step, i) => (
              <li
                key={step.id}
                aria-current={step.state === "current" ? "step" : undefined}
                className="flex gap-3 px-4 py-3"
              >
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                    step.state === "done"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                      : step.state === "current"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {step.state === "done" ? <Check className="size-3.5" /> : i + 1}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="space-y-0.5">
                    <p className={cn("text-sm font-medium", step.state === "waiting" && "text-muted-foreground")}>
                      {step.title}
                    </p>
                    <p className="text-xs text-muted-foreground">{detail(step)}</p>
                  </div>
                  {action(step)}
                </div>
              </li>
            ))}
          </ol>
        )}
      </ConnectionSection>
      {/* Someone who can't attach this Boo would only find the explanation again. */}
      {(app.canMoveHere || app.canAttach) && (
        <Collapsible>
          <CollapsibleTrigger className="group flex items-center gap-1 rounded-sm text-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50">
            <ChevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
            Use the Boo app here instead
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-3">
            <BooAppPanel app={app} usage="" pitch={null} />
          </CollapsibleContent>
        </Collapsible>
      )}
      <ConnectSlackDialog open={linkOpen} onClose={() => setLinkOpen(false)} />
    </>
  );
}

function Choice({
  title,
  tag,
  children,
}: {
  title: string;
  tag: { label: string; tone: keyof typeof TAG_TONE } | null;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-background p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{title}</span>
        {tag && (
          <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium", TAG_TONE[tag.tone])}>
            {tag.label}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

// The Boo's own Slack app, once connected: its name (editable) and workspace.
function OwnAppDetails({ container, status }: { container: ContainerRow; status: SlackStatus | undefined }) {
  const queryClient = useQueryClient();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  const rename = useMutation({
    mutationFn: (next: string) => api.slack.custom.rename(container.id, next),
    onSuccess: (res) => {
      // The PATCH returns the name Slack now has, so update the cache directly
      // rather than invalidating: an invalidate would trigger another sync=true
      // fetch and round-trip the Tier-3 export API for no new info.
      queryClient.setQueryData<SlackStatus>(["slack", container.id], (prev) =>
        prev ? { ...prev, botName: res.botName } : prev,
      );
      setRenaming(false);
      toast.success(`Now appears in Slack as “${res.botName}”`);
    },
  });
  const canRename = status?._permissions?.canRename === true;
  const trimmed = name.trim();
  const valid = trimmed.length >= 2 && trimmed.length <= 30;

  return (
    <ConnectionSection title="Your Slack app" description="Boo answers in Slack under your app's name and icon.">
      <DetailList>
        <DetailRow
          term="Name in Slack"
          action={
            canRename && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setName(status?.botName ?? "");
                  setRenaming((v) => !v);
                }}
              >
                {renaming ? "Cancel" : "Edit name"}
              </Button>
            )
          }
        >
          {status?.botName ?? "Your custom app"}
        </DetailRow>
        {renaming && canRename && (
          <div className="space-y-2 px-4 py-3">
            <Label htmlFor={`slack-rename-${container.id}`} className="text-xs">
              New name
            </Label>
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) rename.mutate(trimmed);
              }}
            >
              <Input
                id={`slack-rename-${container.id}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Assistant"
                maxLength={30}
                aria-invalid={!valid}
                className="sm:max-w-xs"
              />
              <Button type="submit" disabled={!valid || rename.isPending}>
                {rename.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Save
              </Button>
            </form>
            {rename.isError && <InlineError error={rename.error} />}
          </div>
        )}
        <DetailRow term="Workspace">{status?.teamName ?? "Your Slack workspace"}</DetailRow>
      </DetailList>
      <p className="text-xs text-muted-foreground">
        To change the icon, open the app in{" "}
        <a
          href="https://api.slack.com/apps"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-2 hover:text-foreground"
        >
          Slack app settings
        </a>{" "}
        → Basic Information → Display Information.
      </p>
    </ConnectionSection>
  );
}

type InstallOwnMutation = ReturnType<typeof useMutation<{ url: string }, Error, string>>;

// Name the Boo's own Slack app and install it. Needs the workspace token,
// which owners and admins link in the org's Connections → Slack.
function OwnAppForm({
  container,
  workspaceReady,
  workspaceLoading,
  workspaceError,
  install,
  autoFocus = false,
}: {
  container: ContainerRow;
  workspaceReady: boolean;
  workspaceLoading: boolean;
  workspaceError: unknown;
  install: InstallOwnMutation;
  autoFocus?: boolean;
}) {
  const [name, setName] = useState("");
  if (workspaceLoading) return <LoadingIndicator message="Checking your workspace…" />;
  if (workspaceError) return <InlineError error={workspaceError} />;
  if (!workspaceReady) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Your organization hasn&apos;t linked its Slack workspace for custom apps yet.
        </p>
        <Button asChild size="sm" variant="outline" className="w-fit">
          <Link
            to="/orgs/$orgId/connections/$connection"
            params={{ orgId: container.organizationId, connection: "slack" }}
          >
            Link the workspace
          </Link>
        </Button>
      </div>
    );
  }
  const trimmed = name.trim();
  const valid = trimmed.length >= 2 && trimmed.length <= 30;
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) install.mutate(trimmed);
      }}
    >
      <Label htmlFor={`slack-name-${container.id}`} className="text-xs">
        Name in Slack
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={`slack-name-${container.id}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Acme Assistant"
          maxLength={30}
          aria-invalid={!valid}
          autoFocus={autoFocus}
        />
        <Button type="submit" disabled={!valid || install.isPending}>
          {install.isPending && <Loader2 className="size-3.5 animate-spin" />}
          Connect
        </Button>
      </div>
      {install.isError && <InlineError error={install.error} />}
    </form>
  );
}
